import each from 'lodash/each';
import merge from 'lodash/merge';
import { authenticationReady, componentLoader, configurationReady, mdsCssReady, mdsIconsReady, loadFiles, manifestReady } from 'loader-base';
import configurationReadyQs from './configuration-qs';
import ecReady from './initialize-ec';
import { initializeLazyBundleLoader, initializeLazyLoader, lazyDownloadComponent, registerPaths } from './lazy-loader';
import { bundlesReady } from './bundle-loader';
import { installTrackingAgent, setCustomAttributesForNewRelic } from './usage-tracking-helper';
import setDefaultValuesEc from './default-values-ec';
import { getAppContext } from '../../loader-base/src/helpers/app-context-helper';

let lazyDownloading = false;
let lazyBundleDownloading = false;
let savedLoaderConfig;
let manifest = {};

/**
 * Add client custom stylesheets
 * @param {*} manifest
 */
function loadCustomStyles(css) {
    return loadFiles(css.customStyles)
        .then(() => Promise.resolve());
}

function checkAndInitializeMorningstarUsageTracking(loaderConfig) {
    if (loaderConfig.morningstarUsageTracking && loaderConfig.morningstarUsageTracking.enabled) {
        installTrackingAgent(loaderConfig.morningstarUsageTracking.applicationId, () => {
            setCustomAttributesForNewRelic(getAppContext(loaderConfig));
        });
    }
}

function load(loaderConfig = {}) {
    setDefaultValuesEc(loaderConfig);
    if (loaderConfig.lazy && (loaderConfig.lazy.lazyDownloadComponent || loaderConfig.lazy.lazyLoadComponent || loaderConfig.lazy.bundlesToDownload)) {
        loaderConfig.lazy.lazyLoadComponent = true;
        loaderConfig.angular.bootstrap = false;
    }

    let promiseChain = Promise.resolve();

    // NewRelic agent needs to be installed in <head> section
    // this should be done in the sync code and not wait for download of config or bundle
    checkAndInitializeMorningstarUsageTracking(loaderConfig);

    // Get configuration...
    switch (loaderConfig.configuration.source.type) {
        case 'service': {
            promiseChain = promiseChain.then(() => authenticationReady(loaderConfig.apiTokens))
                .then((newApiTokens) => {
                    loaderConfig.apiTokens = merge(loaderConfig.apiTokens, newApiTokens);
                    return configurationReady(loaderConfig.configuration, loaderConfig.apiTokens.apiGatewayToken);
                });
            break;
        }
        case 'qs':
        case 'mwc': {
            promiseChain = promiseChain.then(() => configurationReadyQs(loaderConfig.configuration));
            break;
        }
        case 'none':
        default: {
            promiseChain = promiseChain.then(() => configurationReady(loaderConfig.configuration, loaderConfig.apiTokens.apiGatewayToken));
            break;
        }
    }
    // ...and set configuration
    promiseChain = promiseChain.then((config) => {
        loaderConfig.config = config;
        return true;
    });

    // Download MDS icons/style if set in config
    promiseChain = promiseChain.then(() => mdsIconsReady(loaderConfig.css.mds, loaderConfig.componentSource.environment))
        .then(() => mdsCssReady(loaderConfig.css.mds, loaderConfig.componentSource.environment));

    switch (loaderConfig.componentSource.type) {
        case 'resolver': {
            const asterixSetupFilter = function asterixSetupFilter(file) {
                return (file.indexOf('angular') > -1 || file.indexOf('asterix-') > -1 || file.indexOf('jquery') > -1 || file.indexOf('vue') > -1 || file.indexOf('underscore') > -1);
            };
            const mwsFilter = function mwsFilter(file) {
                return file.indexOf('vue-') < 0 && file.indexOf('mws') < 0;
            };
            const antiMwsFilter = function antiMwsFilter(file) {
                return file.indexOf('vue-') > -1;
            };
            promiseChain = promiseChain.then(() => manifestReady(loaderConfig.componentSource, loaderConfig.config.components))
                .then((manifestContent) => {
                    manifest = manifestContent;
                    return componentLoader.componentInitializationReady(manifest.componentDependencies, manifest.resolutions);
                });

            if (loaderConfig.lazy.lazyDownloadComponent) {
                lazyDownloading = true;
                promiseChain = promiseChain.then(() => initializeLazyLoader(manifest.componentDependencies, manifest.resolutions, loaderConfig.config))
                    .then(() => componentLoader.componentsReady(manifest.resolutions, loaderConfig.css, asterixSetupFilter))
                    .then(() => ecReady(manifest.resolutions, loaderConfig.config, loaderConfig))
                    .then(() => componentLoader.mwsReady(loaderConfig.config, manifest.resolutions));
            } else {
                promiseChain = promiseChain.then(() => componentLoader.componentsReady(manifest.resolutions, loaderConfig.css, mwsFilter))
                    .then(() => ecReady(manifest.resolutions, loaderConfig.config, loaderConfig))
                    .then(() => componentLoader.mwsReady(loaderConfig.config, manifest.resolutions)) // Needs to be loaded after asterix so the config module can be reused.
                    .then(() => componentLoader.componentsReady(manifest.resolutions, loaderConfig.css, antiMwsFilter));
            }
            break;
        }
        case 'qs':
        case 'mwc':
        default: {
            promiseChain = promiseChain.then(() => bundlesReady(loaderConfig, componentLoader.getLoadedFiles()))
            .then((manifestContent) => {
                manifest = manifestContent;
                let returnPromise = Promise.resolve();
                if (loaderConfig.additionalComponents && loaderConfig.additionalComponents.length) {
                    const components = {};
                    loaderConfig.additionalComponents.forEach((element) => {
                        components[element] = { type: element };
                    });
                    loaderConfig.componentSource.type = 'resolver';
                    // TODO: Get metadata info from bundle(s) and include as overrides
                    // loaderConfig.componentSource.overrides = morningstar.asterix.meta.get('NAME_OF_BUNDLE', 'bundleInfo').dependencies;
                    returnPromise = returnPromise.then(() => manifestReady(loaderConfig.componentSource, components).then((result) => {
                        Object.keys(result.componentDependencies).forEach((key) => {
                            const dependencyList = result.componentDependencies[key];
                            dependencyList.splice(0, dependencyList.length, ...dependencyList.filter(dep => !loaderConfig.ignore.includes(dep)));
                          });
                          if (loaderConfig.lazy.lazyDownloadAdditionalComponent) {
                            lazyDownloading = true;
                            return initializeLazyLoader(result.componentDependencies, result.resolutions, loaderConfig.config);
                          }
                          return componentLoader.componentsReady(result.resolutions.filter(dep => !loaderConfig.ignore.includes(dep.name)), loaderConfig.css);
                    }));
                }
                returnPromise = returnPromise.then(() => ecReady(manifest.resolutions, loaderConfig.config, loaderConfig));
                return returnPromise;
            });
            if (loaderConfig.lazy.bundlesToDownload) {
                lazyBundleDownloading = true;
                savedLoaderConfig = loaderConfig;
                promiseChain = promiseChain.then(() => initializeLazyBundleLoader(loaderConfig.config));
            }
            break;
        }
    }

    promiseChain = promiseChain.then(() => loadCustomStyles(loaderConfig.css));
    // For backwards compatible reasons
    manifest.loaderConfig = loaderConfig;
    promiseChain = promiseChain.then(() => Promise.resolve(manifest));
    return promiseChain;
}

function reset() {
    componentLoader.resetInternal();
}

function createAndReturnTag(componentTagName, componentId, containerId) {
    // Create component tag...
    const componentTag = document.createElement(componentTagName);
    componentTag.setAttribute('mstar-component-id', componentId);
    // ... and attach it to the container.
    const container = document.createElement('div');
    container.setAttribute('id', containerId);
    container.appendChild(componentTag);
    return container;
}

function registerAndResetAsterix(componentId) {
    // Register default config for component types that need it.
    each(morningstar.components._defaultConfig, (defConfig, typeName) => {
        if (!morningstar.asterix.config.getDefaultConfig(typeName)) {
            morningstar.asterix.config.addType(typeName, defConfig);
        }
    });
    // Register component paths in Asterix.
    registerPaths(componentId);
    // Handle Asterix reset/init.
    const path = morningstar.asterix.instanceRegistry.find(componentId).modulePath;
    morningstar.asterix.instanceRegistry.init(path);
}

function lazyLoad(containerParent, containerId, componentTagName, componentId) {
    if (lazyDownloading && componentTagName.indexOf('ec-') !== 0) {
        morningstar.asterix.config.setOption('dynamic', true);
        return lazyDownloadComponent(componentTagName, componentLoader.getLoadedFiles());
    } else if (lazyDownloading) {
        return new Promise((resolve) => {
            lazyDownloadComponent(componentTagName, componentLoader.getLoadedFiles()).then(() => {
                morningstar.asterix.config.setOption('dynamic', true);
                const container = createAndReturnTag(componentTagName, componentId, containerId, containerParent);
                registerAndResetAsterix(componentId);
                // Bootstrat the element
                angular.bootstrap(container, morningstar.asterix.ng.getAngularModuleDependencies());
                containerParent.appendChild(container);
                resolve();
            });
        });
    } else if (lazyBundleDownloading) {
        return new Promise((resolve) => {
            savedLoaderConfig.lazy.bundlesToDownload = {};
            savedLoaderConfig.lazy.bundlesToDownload[componentTagName] = true;
            bundlesReady(savedLoaderConfig, componentLoader.getLoadedFiles()).then(() => {
                morningstar.asterix.config.setOption('dynamic', true);
                const container = createAndReturnTag(componentTagName, componentId, containerId, containerParent);
                registerAndResetAsterix(componentId);
                // Bootstrap the element
                angular.bootstrap(container, morningstar.asterix.ng.getAngularModuleDependencies());
                containerParent.appendChild(container);
                resolve();
            });
        });
    }

    return new Promise((resolve) => {
        // Handle Asterix reset/init.
        const path = morningstar.asterix.instanceRegistry.find(componentId).modulePath;
        morningstar.asterix.instanceRegistry.init(path);
        const container = createAndReturnTag(componentTagName, componentId, containerId, containerParent);
        // Bootstrap the element
        angular.bootstrap(container, morningstar.asterix.ng.getAngularModuleDependencies());
        containerParent.appendChild(container);
        resolve();
    });
}

function lazyRemove(containerId) {
    const container = document.getElementById(containerId);
    if (container && container.remove) {
        container.remove();
        console.log(`Removed container with id: ${containerId}`);
    }
    // Because IE
    if (container && container.parentNode) {
        container.parentNode.removeChild(container);
        console.log(`Removed container with id: ${containerId} (IE style`);
    }
}

export default {
    lazyLoad,
    lazyRemove,
    load,
    reset,
};



// WEBPACK FOOTER //
// ./src/loader.js